'use client';

import { motion, useScroll, useTransform } from 'framer-motion';
import { Target, Search, Shield, Upload, Mail, Phone, MapPin, Clock } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Image from 'next/image';
import Link from 'next/link';
import {
  ArrowRightIcon,
  DocumentTextIcon,
  ChartBarIcon,
  LightBulbIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { FaTwitter, FaLinkedin, FaGithub, FaFacebook } from 'react-icons/fa';
import {
  FaMicrosoft,
  FaGoogle,
  FaMeta,
  FaAmazon,
  FaAirbnb,
  FaApple
} from 'react-icons/fa6';
import { SiNetflix, SiAdobe } from 'react-icons/si';

// Animation variants
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.8 }
};

const features = [
  {
    name: 'ATS Score Analysis',
    description: 'Get a detailed score showing how well your resume performs in Applicant Tracking Systems and identify areas for improvement.',
    icon: ChartBarIcon,
    color: 'text-sky-500',
  },
  {
    name: 'Keyword Optimization',
    description: 'Identify missing keywords from job descriptions to improve your resume\'s visibility and match rate.',
    icon: Search,
    color: 'text-blue-500',
  },
  {
    name: 'AI Suggestions',
    description: 'Receive actionable recommendations to enhance your resume content and formatting for better ATS results.',
    icon: LightBulbIcon,
    color: 'text-purple-500',
  },
  {
    name: 'Real-Time Preview',
    description: 'See how your resume looks to ATS systems and make adjustments on the fly.',
    icon: DocumentTextIcon,
    color: 'text-green-500',
  },
];

const steps = [
  {
    name: 'Upload Your Resume',
    description: 'Upload your resume in PDF, DOCX, or TXT format.',
    number: '1',
  },
  {
    name: 'Paste Job Description',
    description: 'Add the job description you\'re applying for.',
    number: '2',
  },
  {
    name: 'Get Instant Analysis',
    description: 'Receive a detailed report with your ATS score and improvement suggestions.',
    number: '3',
  },
];

// Add keyframe animation for gradient background
const keyframes = `
  @keyframes gradient {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

export default function Home() {
  return (
    <div className="min-h-screen bg-white">
      <style>{keyframes}</style>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-white pt-40 sm:pt-48 lg:pt-56 pb-24 overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-sky-50/50 -z-10" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left side - Content */}
            <motion.div
              className="max-w-2xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <motion.h1
                className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 leading-tight mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
              >
                Get your resume <span className="text-sky-600">ATS-ready in seconds</span>
              </motion.h1>

              <motion.p
                className="text-lg md:text-xl text-gray-600 mb-10 leading-relaxed"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: 'easeOut', delay: 0.2 }}
              >
                Beat the ATS and land more interviews with our AI-powered resume optimization tool. Get instant feedback and improve your resume's ATS compatibility.
              </motion.p>

              <motion.div
                className="flex flex-col sm:flex-row gap-4 mb-6"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: 'easeOut', delay: 0.3 }}
              >
                <Link
                  href="/analyze"
                  className="inline-flex items-center justify-center px-7 py-3 text-lg font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:scale-[1.02] transition-all duration-200"
                >
                  Analyze Resume Now
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>

                <Link
                  href="#features"
                  className="inline-flex items-center justify-center px-7 py-3 text-lg font-semibold rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 hover:scale-[1.02] transition-all duration-200"
                >
                  Learn More
                </Link>
              </motion.div>

              <motion.p
                className="text-xs md:text-sm text-gray-400 mt-3"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: 'easeOut', delay: 0.4 }}
              >
                Trusted by 10,000+ job seekers. No credit card required.
              </motion.p>
            </motion.div>

            {/* Right side - Image */}
            <motion.div
              className="relative lg:pl-8 xl:pl-16 mt-12 lg:mt-0"
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ type: 'spring', stiffness: 100, damping: 20, delay: 0.3 }}
            >
              <div className="relative">
                {/* Main Resume Card */}
                <div className="relative z-10 bg-white rounded-2xl shadow-2xl border border-gray-100 p-6 md:p-8 max-w-md mx-auto transform rotate-[-2deg]">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-8">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Alex Morgan</h3>
                      <p className="text-sm text-gray-500">Full stack software engineer</p>
                    </div>
                    <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                      <span className="text-xs text-gray-500">AM</span>
                    </div>
                  </div>

                  {/* Content Sections */}
                  <div className="space-y-6">
                    {/* Summary */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-sm font-semibold text-gray-900">Summary</h4>
                        <div className="h-4 w-4 rounded-full bg-green-100 flex items-center justify-center">
                          <svg className="w-2.5 h-2.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-2 bg-gray-100 rounded w-full"></div>
                        <div className="h-2 bg-gray-100 rounded w-5/6"></div>
                        <div className="flex gap-2">
                          <div className="h-2 bg-green-100 rounded w-1/3"></div>
                          <div className="h-2 bg-gray-100 rounded w-1/4"></div>
                        </div>
                      </div>
                    </div>

                    {/* Experience */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-sm font-semibold text-gray-900">Experience</h4>
                        <div className="h-4 w-4 rounded-full bg-green-100 flex items-center justify-center">
                          <svg className="w-2.5 h-2.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="pl-3 border-l-2 border-gray-100">
                          <div className="h-2 bg-gray-100 rounded w-1/4 mb-2"></div>
                          <div className="h-2 bg-green-100 rounded w-1/2 mb-1"></div>
                          <div className="h-2 bg-gray-100 rounded w-3/4"></div>
                        </div>
                        <div className="pl-3 border-l-2 border-gray-100">
                          <div className="h-2 bg-gray-100 rounded w-1/3 mb-2"></div>
                          <div className="h-2 bg-gray-100 rounded w-2/3"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Score Card Floating */}
                <motion.div
                  className="absolute -right-4 top-20 z-20 bg-white rounded-xl shadow-xl border border-gray-100 p-4 w-48"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.8 }}
                >
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-sm font-bold text-gray-900">Overall Score</span>
                    <div className="relative h-10 w-10 flex items-center justify-center">
                      <svg className="h-full w-full transform -rotate-90" viewBox="0 0 36 36">
                        <path className="text-gray-100" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" />
                        <path className="text-green-500" strokeDasharray="95, 100" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" />
                      </svg>
                      <span className="absolute text-xs font-bold text-gray-900">95%</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Keywords</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full" style={{ width: '90%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Structure</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full" style={{ width: '95%' }}></div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Sparkle Icon Floating */}
                <motion.div
                  className="absolute -top-6 -right-6 z-20 bg-white p-3 rounded-full shadow-lg border border-sky-100"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
                >
                  <svg className="w-8 h-8 text-sky-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 1l2.5 6.5L19 10l-6.5 2.5L10 19l-2.5-6.5L1 10l6.5-2.5z" />
                  </svg>
                </motion.div>

                {/* Decorative Elements */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-br from-sky-100/50 to-purple-100/50 rounded-full blur-3xl -z-10"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Feature Spotlight Section */}
      <section className="py-24 bg-gradient-to-br from-sky-50 via-blue-50 to-purple-50" id="features">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-6">
            <span className="text-sm font-semibold text-sky-600 uppercase tracking-wider">FEATURES</span>
            <h2 className="text-5xl font-bold text-gray-900 mt-4 mb-6">
              Simplify Every Step <span className="text-sky-600">of Your Job Search</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mt-16">
            {/* Left side - Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-sky-200 mb-6">
                <ChartBarIcon className="w-5 h-5 text-sky-600" />
                <span className="text-sm font-semibold text-sky-600 uppercase tracking-wider">AI RESUME BUILDER</span>
              </div>

              <h3 className="text-4xl font-bold text-gray-900 mb-6">
                Instantly build a job-ready resume with AI
              </h3>

              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Use AI to make your resume ATS-friendly, boost your score, and add job-specific keywords in just a few clicks.
              </p>

              <ul className="space-y-4 mb-8">
                {[
                  "Analyze your resume against ATS requirements",
                  "Get instant keyword optimization suggestions",
                  "Improve formatting for better parsing",
                  "Track your progress with detailed scoring"
                ].map((feature, index) => (
                  <motion.li
                    key={index}
                    className="flex items-start gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-700 text-lg">{feature}</span>
                  </motion.li>
                ))}
              </ul>

              <Link
                href="/analyze"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg bg-sky-600 hover:bg-sky-700 text-white shadow-lg hover:scale-105 transition-all duration-200"
              >
                Create My Resume
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
            </motion.div>

            {/* Right side - Visual */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 p-8 transform rotate-2">
                <div className="space-y-6">
                  {/* Summary Section */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-lg font-bold text-gray-900">Summary</h4>
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                        <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-3 bg-gray-100 rounded-full w-full"></div>
                      <div className="h-3 bg-gray-100 rounded-full w-5/6"></div>
                      <div className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full font-semibold">
                        achieve in marketing goals
                      </div>
                    </div>
                  </div>

                  {/* Experience Section */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-lg font-bold text-gray-900">Experience</h4>
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                        <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="pl-3 border-l-2 border-green-200">
                        <div className="text-sm font-semibold text-gray-900 mb-1">Marketing Lead at Google</div>
                        <div className="inline-block px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded font-medium mb-2">
                          with traffic
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full w-3/4"></div>
                      </div>
                    </div>
                  </div>

                  {/* Education Section */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-lg font-bold text-gray-900">Education</h4>
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                        <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">Mismatch Results</div>
                  </div>
                </div>

                {/* Floating Score Card */}
                <motion.div
                  className="absolute -right-6 -top-6 bg-white rounded-xl shadow-2xl border border-gray-200 p-6 w-56"
                  initial={{ scale: 0.8, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                >
                  <div className="text-center mb-4">
                    <div className="text-sm font-semibold text-gray-600 mb-2">Overall Score</div>
                    <div className="relative inline-flex items-center justify-center w-20 h-20">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                        <path className="text-gray-100" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" />
                        <path className="text-green-500" strokeDasharray="95, 100" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" />
                      </svg>
                      <span className="absolute text-2xl font-bold text-gray-900">95%</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600">Keyword Usage</span>
                        <span className="text-gray-900 font-semibold">90%</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-green-400 to-green-600 rounded-full" style={{ width: '90%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600">Resume Structure</span>
                        <span className="text-gray-900 font-semibold">98%</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-green-400 to-green-600 rounded-full" style={{ width: '98%' }}></div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Decorative sparkle */}
              <motion.div
                className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-br from-sky-400 to-blue-600 rounded-full opacity-20 blur-2xl"
                animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.3, 0.2] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Trusted by Job Seekers Worldwide</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Join thousands who've improved their resumes and landed their dream jobs
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {[
              { value: '10,000+', label: 'Resumes Analyzed' },
              { value: '95%', label: 'Success Rate' },
              { value: '4.8/5', label: 'Rating' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
                {stat.label === 'Rating' && (
                  <div className="flex justify-center mt-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg
                        key={star}
                        className={`w-5 h-5 ${star <= 4 ? 'text-yellow-400' : 'text-gray-300'}`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Company Logos */}
          <div className="mt-20 border-t border-gray-100 pt-16 overflow-hidden">
            <p className="text-center text-sm font-semibold text-gray-500 uppercase tracking-wider mb-12">Trusted by job seekers who've landed at top companies</p>

            <div className="relative flex overflow-x-hidden group">
              <div className="flex space-x-16 animate-marquee whitespace-nowrap py-4">
                {[
                  { Icon: FaApple, name: 'Apple' },
                  { Icon: FaMicrosoft, name: 'Microsoft' },
                  { Icon: SiAdobe, name: 'Adobe' },
                  { Icon: FaGoogle, name: 'Google' },
                  { Icon: FaMeta, name: 'Meta' },
                  { Icon: SiNetflix, name: 'Netflix' },
                  { Icon: FaAmazon, name: 'Amazon' },
                  { Icon: FaAirbnb, name: 'Airbnb' },
                  { Icon: FaApple, name: 'Apple' },
                  { Icon: FaMicrosoft, name: 'Microsoft' },
                  { Icon: SiAdobe, name: 'Adobe' },
                  { Icon: FaGoogle, name: 'Google' },
                  { Icon: FaMeta, name: 'Meta' },
                  { Icon: SiNetflix, name: 'Netflix' },
                  { Icon: FaAmazon, name: 'Amazon' },
                  { Icon: FaAirbnb, name: 'Airbnb' },
                ].map((company, index) => (
                  <div
                    key={`${company.name}-${index}`}
                    className="flex items-center justify-center grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-300 transform hover:scale-110 mx-8"
                  >
                    <company.Icon className="h-10 w-auto text-gray-400 hover:text-gray-900" title={company.name} />
                  </div>
                ))}
              </div>

              <div className="absolute top-0 flex space-x-16 animate-marquee2 whitespace-nowrap py-4">
                {[
                  { Icon: FaApple, name: 'Apple' },
                  { Icon: FaMicrosoft, name: 'Microsoft' },
                  { Icon: SiAdobe, name: 'Adobe' },
                  { Icon: FaGoogle, name: 'Google' },
                  { Icon: FaMeta, name: 'Meta' },
                  { Icon: SiNetflix, name: 'Netflix' },
                  { Icon: FaAmazon, name: 'Amazon' },
                  { Icon: FaAirbnb, name: 'Airbnb' },
                  { Icon: FaApple, name: 'Apple' },
                  { Icon: FaMicrosoft, name: 'Microsoft' },
                  { Icon: SiAdobe, name: 'Adobe' },
                  { Icon: FaGoogle, name: 'Google' },
                  { Icon: FaMeta, name: 'Meta' },
                  { Icon: SiNetflix, name: 'Netflix' },
                  { Icon: FaAmazon, name: 'Amazon' },
                  { Icon: FaAirbnb, name: 'Airbnb' },
                ].map((company, index) => (
                  <div
                    key={`${company.name}-${index}-duplicate`}
                    className="flex items-center justify-center grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-300 transform hover:scale-110 mx-8"
                  >
                    <company.Icon className="h-10 w-auto text-gray-400 hover:text-gray-900" title={company.name} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Before vs After Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-sky-600 uppercase tracking-wider">TRANSFORMATION</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-4">Stop Struggling, Start Succeeding</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              See how Get ATS Ready transforms your job search experience
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Before Column */}
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Before Get ATS Ready</h3>
              </div>

              <div className="space-y-4">
                {[
                  {
                    icon: "📄",
                    title: "Rejection",
                    description: "Non-compliant resumes lead to rejections"
                  },
                  {
                    icon: "⏳",
                    title: "Time Wasted",
                    description: "Job searching is a time-consuming task"
                  },
                  {
                    icon: "🔧",
                    title: "Fragmented Tools",
                    description: "Switching between multiple tools is stressful"
                  }
                ].map((item, index) => (
                  <motion.div
                    key={item.title}
                    className="flex items-start gap-4 p-5 rounded-xl bg-white border-2 border-red-100 hover:border-red-200 transition-colors"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <span className="text-3xl">{item.icon}</span>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                      <p className="text-gray-600 text-sm">{item.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* After Column */}
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">After Get ATS Ready</h3>
              </div>

              <div className="space-y-4">
                {[
                  {
                    icon: "✅",
                    title: "No More Rejections",
                    description: "Instantly create ATS-friendly resumes"
                  },
                  {
                    icon: "⚡",
                    title: "Save Time",
                    description: "AI tools simplify your job search"
                  },
                  {
                    icon: "🎯",
                    title: "All in One Solution",
                    description: "Manage your entire job search in one platform"
                  }
                ].map((item, index) => (
                  <motion.div
                    key={item.title}
                    className="flex items-start gap-4 p-5 rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 hover:border-green-300 transition-colors"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <span className="text-3xl">{item.icon}</span>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                      <p className="text-gray-600 text-sm">{item.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-white" id="how-it-works">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">How It Works</h2>
            <p className="text-xl text-gray-600">Get ATS-ready in 3 simple steps</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              {
                step: 1,
                title: "Upload Resume",
                description: "Upload your resume in PDF or DOCX format",
                icon: Upload,
                color: "text-sky-600",
                bg: "bg-sky-50"
              },
              {
                step: 2,
                title: "Paste Job Description",
                description: "Add the job description you're applying for",
                icon: DocumentTextIcon,
                color: "text-blue-600",
                bg: "bg-blue-50"
              },
              {
                step: 3,
                title: "Get Instant Analysis",
                description: "Receive detailed ATS score and improvement suggestions",
                icon: LightBulbIcon,
                color: "text-purple-600",
                bg: "bg-purple-50"
              }
            ].map((step, index) => (
              <motion.div
                key={step.step}
                className="relative group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
              >
                <div className="absolute -inset-4 rounded-2xl bg-gradient-to-r from-gray-50 to-gray-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
                <div className="flex flex-col items-center text-center">
                  <div className={`w-20 h-20 rounded-2xl ${step.bg} flex items-center justify-center mb-8 shadow-sm group-hover:scale-110 transition-transform duration-300`}>
                    <step.icon className={`w-10 h-10 ${step.color}`} />
                  </div>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-900 text-white text-sm font-bold mb-6">
                    {step.step}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed max-w-xs">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Features Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Coming Soon</h2>
            <p className="text-lg text-gray-600">
              We're working hard to bring you even more powerful tools to help you land your dream job.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "LinkedIn Profile Optimization",
                description: "Get AI-powered suggestions to improve your LinkedIn profile visibility.",
                icon: FaLinkedin,
                color: "text-blue-700",
                bg: "bg-blue-50"
              },
              {
                title: "Cover Letter Generator",
                description: "Create tailored cover letters for each job application in seconds.",
                icon: DocumentTextIcon,
                color: "text-green-600",
                bg: "bg-green-50"
              },
              {
                title: "Interview Prep Coach",
                description: "Practice with AI-generated interview questions specific to your target role.",
                icon: UserGroupIcon,
                color: "text-purple-600",
                bg: "bg-purple-50"
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <div className={`w-12 h-12 rounded-xl ${feature.bg} flex items-center justify-center mb-6`}>
                  <feature.icon className={`w-6 h-6 ${feature.color}`} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 mb-6">{feature.description}</p>
                <div className="inline-flex items-center text-sm font-medium text-sky-600 bg-sky-50 px-3 py-1 rounded-full">
                  Coming Q4 2025
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Feedback Section */}
      <section className="py-24 bg-gray-50 border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Have a feature in mind?</h2>
            <p className="text-lg text-gray-600 mb-8">
              We're constantly improving. Let us know what features you'd like to see next.
            </p>
            <form className="max-w-md mx-auto">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="I wish I could..."
                  className="flex-1 rounded-lg border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 px-4 py-3"
                />
                <button
                  type="submit"
                  className="bg-gray-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors"
                >
                  Send
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 pt-16 pb-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-2">
              <Link href="/" className="text-2xl font-bold text-gray-900 mb-4 block">
                <span className="text-sky-600">Get ATS Ready</span>
              </Link>
              <p className="text-gray-600 max-w-sm mb-6">
                AI-powered resume optimization to help you beat the ATS and land your dream job.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-gray-900 transition-colors">
                  <FaGithub className="h-6 w-6" />
                </a>
                <a href="#" className="text-gray-400 hover:text-sky-500 transition-colors">
                  <FaTwitter className="h-6 w-6" />
                </a>
                <a href="#" className="text-gray-400 hover:text-blue-700 transition-colors">
                  <FaLinkedin className="h-6 w-6" />
                </a>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Product</h3>
              <ul className="space-y-3">
                <li><Link href="#features" className="text-gray-600 hover:text-sky-600 transition-colors">Features</Link></li>
                <li><Link href="#how-it-works" className="text-gray-600 hover:text-sky-600 transition-colors">How It Works</Link></li>
                <li><Link href="/analyze" className="text-gray-600 hover:text-sky-600 transition-colors">Pricing</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Legal</h3>
              <ul className="space-y-3">
                <li><Link href="#" className="text-gray-600 hover:text-sky-600 transition-colors">Privacy</Link></li>
                <li><Link href="#" className="text-gray-600 hover:text-sky-600 transition-colors">Terms</Link></li>
                <li><Link href="#" className="text-gray-600 hover:text-sky-600 transition-colors">Cookies</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} Get ATS Ready. All rights reserved.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              {/* Empty for now, can add more links if needed */}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
